import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { ArrowDown, ArrowUp, Bell, BellOff, Check, Plus, Printer, QrCode, RefreshCw, Trash2 } from "lucide-react";
import { DEFAULT_BELL_BUTTONS, bellOptionsOf, type BellButton, type DiningTable, type TableCall } from "@mango-moons/domain";
import { api, attempt, toast, useApp, useCan, useData, useSettings } from "../state";
import { activeCloudConfig } from "./cloudConfig";
import { cx, minutesAgo } from "./format";
import { printInBrowser } from "./printing";
import { Button, Input, Modal, Select, Toggle, useBusy } from "./ui";

/**
 * QR table bell inside the POS. A table's QR code opens the POS server's bell page; a customer at a table with an
 * open dine-in bill can call staff. Calls arrive here live (the `tableCalls` collection) with a sound.
 * Needs cloud mode: customers' phones must reach the server.
 */
export const CALL_LABELS: Record<TableCall["type"], string> = { waiter: "Call waiter", water: "Water", bill: "Bring the bill", other: "Other", custom: "Request" };
/** What the customer pressed, as the manager named the button. */
export const callLabel = (call: TableCall) => call.label || CALL_LABELS[call.type];

/** Address in the table's QR code, or null when this POS has no server (local mode) or the table has no QR yet. */
export function bellLink(table: DiningTable): string | null {
  const server = activeCloudConfig()?.apiUrl;
  return server && table.bellKey ? `${server}/bell/t/${encodeURIComponent(table.id)}?k=${table.bellKey}` : null;
}

export const bellAvailable = () => useApp.getState().mode === "firebase" && !!activeCloudConfig()?.apiUrl;

/** Calls not yet done (waiting or received), only for bills that are still open on their table (a paid bill's calls disappear). */
export function useOpenCalls(): TableCall[] {
  const calls = useData("tableCalls"), tables = useData("tables");
  return calls
    .filter((call) => call.status !== "done" && tables.some((table) => table.id === call.tableId && table.activeOrderId === call.orderId))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

let audio: AudioContext | null = null;
function chime() {
  try {
    audio ??= new AudioContext();
    void audio.resume();
    const start = audio.currentTime;
    [1318, 1046, 1318].forEach((frequency, index) => {
      const osc = audio!.createOscillator(), gain = audio!.createGain();
      osc.type = "triangle"; osc.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, start + index * 0.28);
      gain.gain.exponentialRampToValueAtTime(0.8, start + index * 0.28 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + index * 0.28 + 0.6);
      osc.connect(gain).connect(audio!.destination);
      osc.start(start + index * 0.28); osc.stop(start + index * 0.28 + 0.65);
    });
  } catch { /* no audio on this device */ }
}

/** Header bell: count of waiting calls, rings and shows a message for each new one, opens the list. */
export function BellButton() {
  const can = useCan();
  const calls = useOpenCalls();
  const [open, setOpen] = useState(false);
  const known = useRef<Set<string> | null>(null);

  useEffect(() => {
    const ids = new Set(calls.map((call) => call.id));
    if (known.current) {
      const fresh = calls.filter((call) => !known.current!.has(call.id));
      if (fresh.length) {
        chime();
        for (const call of fresh) toast("info", `🔔 ${call.tableName}: ${callLabel(call)}${call.note ? ` — ${call.note}` : ""}`);
      }
    }
    known.current = ids;
  }, [calls]);

  // A call nobody has taken for 3 minutes rings again every minute; one marked received stays quiet.
  useEffect(() => {
    if (!calls.some((call) => call.status === "open")) return;
    const timer = setInterval(() => { if (calls.some((call) => call.status === "open" && Date.now() - Date.parse(call.createdAt) > 180_000)) chime(); }, 60_000);
    return () => clearInterval(timer);
  }, [calls]);

  if (!can("orders.read") || !bellAvailable()) return null;
  return <>
    <button onClick={() => setOpen(true)} aria-label={`Table calls: ${calls.length}`} title="Table calls"
      className={cx("relative grid size-10 place-items-center rounded-full border", calls.some((call) => call.status === "open") ? "animate-pulse border-mango bg-mango/15 text-mango" : calls.length ? "border-mango/50 text-mango" : "border-line text-mute hover:text-cream")}>
      <Bell size={18}/>
      {calls.length > 0 && <span className="absolute -right-1 -top-1 grid min-w-5 place-items-center rounded-full bg-mango px-1 text-[11px] font-bold text-ink">{calls.length}</span>}
    </button>
    {open && <CallsDialog calls={calls} onClose={() => setOpen(false)}/>}
  </>;
}

/** One tap: the call leaves the list and the customer's phone says a staff member has it and is coming. */
export function CallActions({ call, size = "sm" }: { call: TableCall; size?: "sm" | "md" }) {
  const can = useCan();
  if (!can("orders.create")) return null;
  return <Button size={size} variant="primary" icon={Check} onClick={(event) => { event.stopPropagation(); void attempt(() => api("bell.done", { callId: call.id })); }}>Done</Button>;
}

/**
 * Live service: while any table is calling, a panel stays at the side of every screen with each table and its
 * request, so staff answer without opening anything. It hides itself when no table is calling.
 */
export function LiveCallsPanel() {
  const can = useCan(), calls = useOpenCalls();
  const [, tick] = useState(0);
  useEffect(() => { if (!calls.length) return; const timer = setInterval(() => tick((value) => value + 1), 30_000); return () => clearInterval(timer); }, [calls.length]);
  if (!calls.length || !can("orders.read") || !bellAvailable()) return null;
  const tables = [...new Set(calls.map((call) => call.tableId))].map((tableId) => calls.filter((call) => call.tableId === tableId));
  return <aside aria-label="Tables calling" className="no-print fixed bottom-4 right-4 z-40 w-[min(22rem,calc(100vw-2rem))] animate-rise space-y-2">
    <p className="flex items-center gap-2 px-1 text-xs font-bold uppercase tracking-[0.16em] text-mango"><Bell size={14} className="animate-pulse"/>Live service · {calls.length} {calls.length === 1 ? "call" : "calls"}</p>
    <div className="max-h-[60vh] space-y-2 overflow-y-auto">{tables.map((group) => { const late = Date.now() - Date.parse(group[0]!.createdAt) > 180_000;
      return <section key={group[0]!.tableId} className={cx("rounded-2xl border p-3 shadow-lift backdrop-blur", late ? "border-bad/60 bg-[#2a1512]/95" : "border-mango/60 bg-[#241a10]/95")}>
        <p className="mb-1.5 flex items-baseline justify-between gap-2"><strong className="font-display text-xl">{group[0]!.tableName}</strong><small className={cx("text-xs", late ? "text-bad" : "text-mute")}>{minutesAgo(group[0]!.createdAt)}</small></p>
        <ul className="space-y-1.5">{group.map((call) => <li key={call.id} className="flex items-center gap-2">
          <span className="min-w-0 flex-1"><span className="block font-semibold">{callLabel(call)}</span>{call.note && <span className="block truncate text-sm text-mute">“{call.note}”</span>}</span>
          <CallActions call={call}/>
        </li>)}</ul>
      </section>; })}</div>
  </aside>;
}

function CallsDialog({ calls, onClose }: { calls: TableCall[]; onClose: () => void }) {
  return <Modal open onClose={onClose} size="sm" eyebrow="Table bell" title="Table calls" footer={<Button onClick={onClose}>Close</Button>}>
    {calls.length === 0 ? <p className="text-sm text-mute">No table is calling right now.</p>
      : <ul className="space-y-2">{calls.map((call) => {
        const late = call.status === "open" && Date.now() - Date.parse(call.createdAt) > 180_000;
        return <li key={call.id} className={cx("flex items-center gap-3 rounded-xl border px-3.5 py-2.5", late ? "border-bad/50 bg-bad/10" : call.status === "received" ? "border-line bg-coal" : "border-mango/40 bg-mango/10")}>
          <span className="min-w-0 flex-1"><strong className="block">{call.tableName} · {callLabel(call)}</strong>
            <small className="text-xs text-mute">{minutesAgo(call.createdAt)} · bill {call.dailyNumber}{call.status === "received" && call.receivedBy ? ` · ${call.receivedBy} is on the way` : ""}</small>
            {call.note && <span className="mt-1 block text-sm">“{call.note}”</span>}</span>
          <CallActions call={call}/>
        </li>;
      })}</ul>}
  </Modal>;
}

const KIND_LABELS: Record<BellButton["kind"], string> = { waiter: "Calls a waiter", water: "Water", bill: "Brings the bill", other: "Customer types a note", custom: "Your own request" };

/**
 * What the table QR page shows. The manager names each button (English and Sinhala), picks its icon, adds their
 * own requests ("Extra napkins"), removes and reorders them; and decides whether the bill and the menu show.
 */
export function BellOptionsEditor() {
  const saved = bellOptionsOf(useSettings());
  const [buttons, setButtons] = useState<BellButton[]>(saved.buttons);
  const [busy, run] = useBusy();
  const savedKey = JSON.stringify(saved.buttons);
  useEffect(() => setButtons(JSON.parse(savedKey) as BellButton[]), [savedKey]);
  const changed = JSON.stringify(buttons) !== savedKey;
  const update = (index: number, patch: Partial<BellButton>) => setButtons(buttons.map((button, position) => position === index ? { ...button, ...patch } : button));
  const move = (index: number, by: number) => { const next = [...buttons]; const [moved] = next.splice(index, 1); next.splice(index + by, 0, moved!); setButtons(next); };
  const save = (patch: Partial<{ showBill: boolean; showMenu: boolean }> = {}) => void run(() => attempt(() => api("bell.setOptions", { buttons, showBill: saved.showBill, showMenu: saved.showMenu, ...patch }), "The QR page is updated for every table."));
  const missing = DEFAULT_BELL_BUTTONS.filter((standard) => !buttons.some((button) => button.kind === standard.kind));
  return <section className="mb-5 space-y-3 rounded-2xl border border-line bg-coal p-4">
    <p className="eyebrow">Buttons on the QR page</p>
    <ul className="space-y-2">{buttons.map((button, index) => <li key={button.id || index} className="grid grid-cols-[3.2rem_1fr_auto] items-center gap-2 rounded-xl border border-line bg-panel p-2 sm:grid-cols-[3.2rem_1fr_1fr_auto]">
      <Input aria-label="Icon" value={button.icon} maxLength={8} className="text-center text-xl" onChange={(event) => update(index, { icon: event.target.value })}/>
      <Input aria-label="Name in English" value={button.label} maxLength={30} placeholder="Name (English)" onChange={(event) => update(index, { label: event.target.value })}/>
      <Input aria-label="Name in Sinhala" value={button.labelSi} maxLength={40} placeholder="නම (සිංහල)" className="col-start-2 sm:col-start-auto" onChange={(event) => update(index, { labelSi: event.target.value })}/>
      <span className="col-start-3 row-span-2 row-start-1 flex flex-col gap-1 sm:row-span-1 sm:col-start-auto sm:flex-row">
        <Button size="sm" variant="ghost" icon={ArrowUp} aria-label="Move up" disabled={index === 0} onClick={() => move(index, -1)}/>
        <Button size="sm" variant="ghost" icon={ArrowDown} aria-label="Move down" disabled={index === buttons.length - 1} onClick={() => move(index, 1)}/>
        <Button size="sm" variant="ghost" icon={Trash2} aria-label="Remove" disabled={buttons.length === 1} onClick={() => setButtons(buttons.filter((_, position) => position !== index))}/>
      </span>
      <small className="col-span-2 col-start-2 text-xs text-faint sm:col-span-3">{KIND_LABELS[button.kind]}{index === 0 ? " · shown as the big button" : ""}</small>
    </li>)}</ul>
    <div className="flex flex-wrap items-center gap-2">
      <Button size="sm" icon={Plus} disabled={buttons.length >= 8} onClick={() => setButtons([...buttons, { id: "", kind: "custom", label: "", labelSi: "", icon: "⭐" }])}>Add a button</Button>
      {missing.length > 0 && <Select className="w-auto" value="" aria-label="Put back a standard button" onChange={(event) => { const standard = missing.find((entry) => entry.kind === event.target.value); if (standard) setButtons([...buttons, standard]); }}>
        <option value="">Put back a standard button…</option>{missing.map((entry) => <option key={entry.kind} value={entry.kind}>{entry.icon} {entry.label}</option>)}</Select>}
      <span className="flex-1"/>
      {changed && <Button size="sm" variant="ghost" onClick={() => setButtons(saved.buttons)}>Undo changes</Button>}
      <Button size="sm" variant="primary" loading={busy} disabled={!changed || buttons.some((button) => !button.label.trim())} onClick={() => save()}>Save buttons</Button>
    </div>
    <div className="grid gap-2 border-t border-line pt-3 sm:grid-cols-2">
      <Toggle checked={saved.showBill} onChange={(showBill) => save({ showBill })} label="Show the bill" hint="The table sees what it ordered and the total so far, updated every few seconds."/>
      <Toggle checked={saved.showMenu} onChange={(showMenu) => save({ showMenu })} label="Show the menu" hint="Dishes and prices, with sold-out dishes marked."/>
    </div>
  </section>;
}

async function qrSvg(link: string) {
  return QRCode.toString(link, { type: "svg", margin: 2, errorCorrectionLevel: "M" });
}

/** Prints table cards, four per A4 page: name, QR, "Scan to call staff" in English and Sinhala. */
async function printCards(tables: DiningTable[], restaurant: string) {
  const cards = await Promise.all(tables.map(async (table) => {
    const link = bellLink(table);
    return link ? `<div class="card"><div class="r">${escape(restaurant)}</div><div class="t">${escape(table.name)}</div>${await qrSvg(link)}<div class="h">Scan to call staff</div><div class="h si">කාර්ය මණ්ඩලය කැඳවීමට ස්කෑන් කරන්න</div></div>` : "";
  }));
  const html = `<!doctype html><html><head><meta charset="utf-8"><style>
@page{size:A4;margin:10mm}*{box-sizing:border-box}body{margin:0;font-family:system-ui,"Segoe UI","Noto Sans Sinhala","Iskoola Pota",sans-serif}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:8mm}.card{border:1.2mm solid #ff9d2f;border-radius:6mm;padding:6mm;text-align:center;break-inside:avoid;height:132mm;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2mm}
.r{font-weight:700;font-size:13pt}.t{font-size:24pt;font-weight:800;color:#e8780a}svg{width:62mm;height:62mm}.h{font-size:11pt;font-weight:600}.si{font-weight:500}
</style></head><body><div class="grid">${cards.join("")}</div></body></html>`;
  await printInBrowser(html);
}

const escape = (text: string) => text.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]!);

/** Tables screen → "QR codes": see, print and replace each table's QR code. */
export function QrCodesDialog({ onClose }: { onClose: () => void }) {
  const tables = useData("tables").filter((table) => table.active).sort((a, b) => a.area.localeCompare(b.area) || a.name.localeCompare(b.name, undefined, { numeric: true }));
  const restaurant = useSettings().name;
  const [busy, run] = useBusy();
  const missing = tables.filter((table) => !table.bellKey);
  const [confirm, setConfirm] = useState<DiningTable | null>(null);
  const createMissing = () => run(async () => { for (const table of missing) await attempt(() => api("tables.newQr", { tableId: table.id })); toast("success", "QR codes created."); });
  return <Modal open onClose={onClose} eyebrow="Table bell" title="QR codes" size="lg"
    footer={<><Button onClick={onClose}>Close</Button>
      {missing.length > 0 && <Button icon={QrCode} loading={busy} onClick={() => void createMissing()}>Create {missing.length} missing</Button>}
      <Button variant="primary" icon={Printer} disabled={tables.every((table) => !table.bellKey)} onClick={() => void printCards(tables.filter((table) => table.bellKey), restaurant)}>Print all</Button></>}>
    <BellOptionsEditor/>
    <p className="mb-4 text-sm text-mute">Put each card on its table. Customers scan it to call staff — it only works while the table has an open bill.
      If a QR code is misused, press <b>New QR</b>: the old card stops working at once, so print and replace it.</p>
    <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
      {tables.map((table) => <QrCard key={table.id} table={table} restaurant={restaurant} onReplace={() => setConfirm(table)}/>)}
    </div>
    {confirm && <Modal open onClose={() => setConfirm(null)} size="sm" eyebrow={confirm.name} title="Replace this QR code?"
      footer={<><Button onClick={() => setConfirm(null)}>Cancel</Button><Button variant="danger" icon={RefreshCw} onClick={() => { void attempt(() => api("tables.newQr", { tableId: confirm.id }), `New QR code for ${confirm.name}. Print it and replace the card.`); setConfirm(null); }}>Replace</Button></>}>
      <p className="text-sm text-mute">The card on {confirm.name} stops working immediately. Print the new one and put it on the table.</p>
    </Modal>}
  </Modal>;
}

function QrCard({ table, restaurant, onReplace }: { table: DiningTable; restaurant: string; onReplace: () => void }) {
  const link = bellLink(table);
  const [svg, setSvg] = useState("");
  useEffect(() => { if (link) void qrSvg(link).then(setSvg); else setSvg(""); }, [link]);
  return <div className="rounded-2xl border border-line bg-panel p-3 text-center">
    <strong className="block">{table.name}</strong>
    <div className="mx-auto my-2 grid aspect-square w-full max-w-36 place-items-center rounded-lg bg-white p-1 [&_svg]:size-full">
      {svg ? <span className="size-full" dangerouslySetInnerHTML={{ __html: svg }}/> : <QrCode className="text-faint" size={40}/>}
    </div>
    {table.bellMuted && <small className="mb-1 flex items-center justify-center gap-1 text-xs text-bad"><BellOff size={12}/> Bell paused</small>}
    <div className="flex justify-center gap-1.5">
      {link ? <><Button size="sm" icon={Printer} onClick={() => void printCards([table], restaurant)}>Print</Button><Button size="sm" variant="ghost" onClick={onReplace}>New QR</Button></>
        : <Button size="sm" icon={QrCode} onClick={() => void attempt(() => api("tables.newQr", { tableId: table.id }))}>Create</Button>}
    </div>
    <button className="mt-1.5 text-xs text-mute underline-offset-2 hover:underline" onClick={() => void attempt(() => api("tables.setBellMuted", { tableId: table.id, muted: !table.bellMuted }))}>
      {table.bellMuted ? "Turn bell back on" : "Pause bell"}
    </button>
  </div>;
}
